// AustralianParliamentVisualizer.js
// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../styles/ParliamentChamberMap.css";
import Parliament3DView from "../components/Parliament3DView";

export default function ParliamentChamberMap() {
  // State
  const [selectedBill, setSelectedBill] = useState("Healthcare Reform");
  const [selectedMp, setSelectedMp] = useState(null);
  const [viewMode, setViewMode] = useState("chamber"); // "chamber", "analytics", "3d"
  const [highlightParty, setHighlightParty] = useState(null);
  const [highlightVote, setHighlightVote] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showInfo, setShowInfo] = useState(true);

  // Sample data
  const bills = [
    { id: "healthcare", name: "Healthcare Reform", date: "March 15, 2025" },
    { id: "climate", name: "Climate Action Bill", date: "February 2, 2025" },
    { id: "budget", name: "Budget 2025", date: "April 5, 2025" },
  ];

  // Party data with expanded properties
  const partyData = {
    Liberal: {
      color: "#003f7e",
      secondaryColor: "#0059b8",
      seats: 68,
      supportCount: 5,
      opposeCount: 60,
      unknownCount: 3,
      leader: "John Anderson",
      founded: 1944,
    },
    Labor: {
      color: "#e42129",
      secondaryColor: "#ff4b52",
      seats: 68,
      supportCount: 65,
      opposeCount: 0,
      unknownCount: 3,
      leader: "Maria Santiago",
      founded: 1901,
    },
    Green: {
      color: "#009c3d",
      secondaryColor: "#00cc50",
      seats: 20,
      supportCount: 20,
      opposeCount: 0,
      unknownCount: 0,
      leader: "Sarah Waters",
      founded: 1992,
    },
    Independent: {
      color: "#ffc107",
      secondaryColor: "#ffda5a",
      seats: 16,
      supportCount: 8,
      opposeCount: 5,
      unknownCount: 3,
      leader: "Various",
      founded: null,
    },
  };

  // Generate MP data
  const generateMps = () => {
    const mps = [];
    const parties = Object.keys(partyData);

    parties.forEach((party) => {
      const partyInfo = partyData[party];
      const seatCount = partyInfo.seats;

      for (let i = 1; i <= seatCount; i++) {
        // Determine vote status based on counts
        let voteStatus;
        if (i <= partyInfo.supportCount) voteStatus = "support";
        else if (i <= partyInfo.supportCount + partyInfo.opposeCount)
          voteStatus = "oppose";
        else voteStatus = "unknown";

        // Generate MP data with more realistic properties
        mps.push({
          id: `${party.toLowerCase()}-${i}`,
          name: `${party} MP ${i}`,
          party: party,
          voteStatus: voteStatus,
          profileImg: null, // Could be URLs to profile images
          yearsServed: Math.floor(Math.random() * 20) + 1,
          constituency: `${party} District ${i}`,
          // Add attendance percentage for an interesting data point
          attendance: Math.floor(Math.random() * 30) + 70,
        });
      }
    });

    return mps;
  };

  const mps = generateMps();

  // Vote outcome data for visualization
  const voteOutcome = {
    support: Object.values(partyData).reduce(
      (acc, party) => acc + party.supportCount,
      0
    ),
    oppose: Object.values(partyData).reduce(
      (acc, party) => acc + party.opposeCount,
      0
    ),
    unknown: Object.values(partyData).reduce(
      (acc, party) => acc + party.unknownCount,
      0
    ),
  };

  // Get vote breakdown for charts
  const getPartyVoteData = () => {
    return Object.keys(partyData).map((party) => ({
      party,
      Support: partyData[party].supportCount,
      Oppose: partyData[party].opposeCount,
      Unknown: partyData[party].unknownCount,
    }));
  };

  const pieData = [
    { name: "Support", value: voteOutcome.support, color: "#70ad47" },
    { name: "Oppose", value: voteOutcome.oppose, color: "#ed7d31" },
    { name: "Unknown", value: voteOutcome.unknown, color: "#ffc000" },
  ];

  // Handle MP selection
  const handleMpSelect = (mp) => {
    setSelectedMp(mp === selectedMp ? null : mp);
  };

  // Filter MPs based on active filter
  const getFilteredMps = () => {
    if (activeFilter === "all") return mps;
    if (activeFilter === "support")
      return mps.filter((mp) => mp.voteStatus === "support");
    if (activeFilter === "oppose")
      return mps.filter((mp) => mp.voteStatus === "oppose");
    if (activeFilter === "unknown")
      return mps.filter((mp) => mp.voteStatus === "unknown");
    if (Object.keys(partyData).includes(activeFilter)) {
      return mps.filter((mp) => mp.party === activeFilter);
    }
    return mps;
  };

  // Render chamber seating layout
  const renderChamberView = () => {
    // Get MPs filtered by active criteria
    const filteredMps = getFilteredMps();

    // Create a map for quickly checking if an MP is filtered
    const filteredMpIds = new Set(filteredMps.map((mp) => mp.id));

    return (
      <div className="chamber-container">
        <div className="president-desk">Speaker's Chair</div>

        <div className="clerks-table">
          <div className="clerk">Clerk</div>
          <div className="deputy-clerk">Deputy Clerk</div>
        </div>

        <div className="chamber-layout">
          {/* Government side (horseshoe arrangement) */}
          <div className="government-side">
            <h3 className="section-heading">Government</h3>
            <div className="seats-arrangement government">
              {mps
                .filter((mp) => mp.party === "Liberal")
                .map((mp) => (
                  <div
                    key={mp.id}
                    className={`seat ${mp.voteStatus} ${mp.party.toLowerCase()} 
                             ${selectedMp === mp ? "selected" : ""} 
                             ${
                               highlightParty === mp.party
                                 ? "highlight-party"
                                 : ""
                             }
                             ${
                               highlightVote === mp.voteStatus
                                 ? "highlight-vote"
                                 : ""
                             }
                             ${
                               !filteredMpIds.has(mp.id) ? "filtered-out" : ""
                             }`}
                    onClick={() => handleMpSelect(mp)}
                    onMouseEnter={() => setHighlightParty(mp.party)}
                    onMouseLeave={() => setHighlightParty(null)}
                  >
                    <span className="seat-label">{mp.id.split("-")[1]}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Opposition side (horseshoe arrangement) */}
          <div className="opposition-side">
            <h3 className="section-heading">Opposition</h3>
            <div className="seats-arrangement opposition">
              {mps
                .filter((mp) => mp.party === "Labor")
                .map((mp) => (
                  <div
                    key={mp.id}
                    className={`seat ${mp.voteStatus} ${mp.party.toLowerCase()} 
                             ${selectedMp === mp ? "selected" : ""}
                             ${
                               highlightParty === mp.party
                                 ? "highlight-party"
                                 : ""
                             }
                             ${
                               highlightVote === mp.voteStatus
                                 ? "highlight-vote"
                                 : ""
                             }
                             ${
                               !filteredMpIds.has(mp.id) ? "filtered-out" : ""
                             }`}
                    onClick={() => handleMpSelect(mp)}
                    onMouseEnter={() => setHighlightParty(mp.party)}
                    onMouseLeave={() => setHighlightParty(null)}
                  >
                    <span className="seat-label">{mp.id.split("-")[1]}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Crossbench (at the top of the horseshoe) */}
          <div className="crossbench">
            <h3 className="section-heading">Crossbench</h3>
            <div className="seats-arrangement crossbench">
              {mps
                .filter(
                  (mp) => mp.party === "Green" || mp.party === "Independent"
                )
                .map((mp) => (
                  <div
                    key={mp.id}
                    className={`seat ${mp.voteStatus} ${mp.party.toLowerCase()} 
                             ${selectedMp === mp ? "selected" : ""}
                             ${
                               highlightParty === mp.party
                                 ? "highlight-party"
                                 : ""
                             }
                             ${
                               highlightVote === mp.voteStatus
                                 ? "highlight-vote"
                                 : ""
                             }
                             ${
                               !filteredMpIds.has(mp.id) ? "filtered-out" : ""
                             }`}
                    onClick={() => handleMpSelect(mp)}
                    onMouseEnter={() => setHighlightParty(mp.party)}
                    onMouseLeave={() => setHighlightParty(null)}
                  >
                    <span className="seat-label">{mp.id.split("-")[1]}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="center-table">Hansard</div>

        <div className="advisors">
          <div className="advisor-block left">Gov Advisors</div>
          <div className="advisor-block right">Opp Advisors</div>
        </div>
      </div>
    );
  };

  // Render analytics view
  const renderAnalyticsView = () => {
    return (
      <div className="analytics-container">
        <div className="charts-grid">
          <div className="chart-panel">
            <h3>Vote Distribution by Party</h3>
            <div className="chart-wrapper bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getPartyVoteData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="party" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="Support"
                    fill="#70ad47"
                    onMouseEnter={() => setHighlightVote("support")}
                    onMouseLeave={() => setHighlightVote(null)}
                  />
                  <Bar
                    dataKey="Oppose"
                    fill="#ed7d31"
                    onMouseEnter={() => setHighlightVote("oppose")}
                    onMouseLeave={() => setHighlightVote(null)}
                  />
                  <Bar
                    dataKey="Unknown"
                    fill="#ffc000"
                    onMouseEnter={() => setHighlightVote("unknown")}
                    onMouseLeave={() => setHighlightVote(null)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-panel">
            <h3>Overall Vote Outcome</h3>
            <div className="chart-wrapper pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    onMouseEnter={(data) =>
                      setHighlightVote(data.name.toLowerCase())
                    }
                    onMouseLeave={() => setHighlightVote(null)}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="insights-panel">
            <h3>Key Insights</h3>
            <ul className="insights-list">
              <li>
                Bill has{" "}
                <strong>
                  {Math.round(
                    (voteOutcome.support /
                      (voteOutcome.support + voteOutcome.oppose)) *
                      100
                  )}
                  %
                </strong>{" "}
                support among voting members
              </li>
              <li>All Green MPs support this legislation</li>
              <li>
                {partyData.Liberal.opposeCount} Liberal MPs oppose the bill
              </li>
              <li>
                Attendance for this vote is at <strong>97%</strong>
              </li>
              <li>
                This vote shows stronger party-line voting than typical for
                healthcare bills
              </li>
            </ul>
          </div>

          <div className="prediction-panel">
            <h3>Vote Outcome Prediction</h3>
            <div
              className={`outcome-display ${
                voteOutcome.support > voteOutcome.oppose ? "pass" : "fail"
              }`}
            >
              {voteOutcome.support > voteOutcome.oppose
                ? "BILL WILL PASS"
                : "BILL WILL FAIL"}
            </div>
            <div className="margin-info">
              Margin: {Math.abs(voteOutcome.support - voteOutcome.oppose)} votes
              (
              {Math.round(
                (Math.abs(voteOutcome.support - voteOutcome.oppose) /
                  (voteOutcome.support +
                    voteOutcome.oppose +
                    voteOutcome.unknown)) *
                  100
              )}
              %)
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render 3D view (placeholder)
  const render3DView = () => {
    return (
      <div className="view-3d-container">
        <Parliament3DView
          mps={mps}
          partyData={partyData}
          selectedMp={selectedMp}
          setSelectedMp={setSelectedMp}
          highlightParty={highlightParty}
          highlightVote={highlightVote}
          activeFilter={activeFilter}
        />
      </div>
    );
  };

  return (
    <div className="parliament-visualizer">
      <div className="header-section">
        <h1>Australian Parliament Voting History</h1>
        <div className="subheading">
          Visualizing the Senate chamber and voting patterns
        </div>

        <div className="control-panel">
          <div className="control-group">
            <label>Bill:</label>
            <select
              value={selectedBill}
              onChange={(e) => setSelectedBill(e.target.value)}
            >
              {bills.map((bill) => (
                <option key={bill.id} value={bill.name}>
                  {bill.name}
                </option>
              ))}
            </select>
          </div>

          <div className="view-toggle">
            <button
              className={viewMode === "chamber" ? "active" : ""}
              onClick={() => setViewMode("chamber")}
            >
              Chamber View
            </button>
            <button
              className={viewMode === "analytics" ? "active" : ""}
              onClick={() => setViewMode("analytics")}
            >
              Analytics
            </button>
            <button
              className={viewMode === "3d" ? "active" : ""}
              onClick={() => setViewMode("3d")}
            >
              3D View
            </button>
          </div>

          <div className="filter-buttons">
            <button
              className={activeFilter === "all" ? "active" : ""}
              onClick={() => setActiveFilter("all")}
            >
              All
            </button>
            <button
              className={
                activeFilter === "support" ? "active support" : "support"
              }
              onClick={() => setActiveFilter("support")}
            >
              Support
            </button>
            <button
              className={activeFilter === "oppose" ? "active oppose" : "oppose"}
              onClick={() => setActiveFilter("oppose")}
            >
              Oppose
            </button>
            <button
              className={
                activeFilter === "unknown" ? "active unknown" : "unknown"
              }
              onClick={() => setActiveFilter("unknown")}
            >
              Unknown
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="visualization-container">
          {viewMode === "chamber" && renderChamberView()}
          {viewMode === "analytics" && renderAnalyticsView()}
          {viewMode === "3d" && render3DView()}

          {showInfo && !selectedMp && (
            <div className="info-overlay">
              <button className="close-info" onClick={() => setShowInfo(false)}>
                ×
              </button>
              <h3>Welcome to the Parliament Visualizer</h3>
              <p>
                Explore voting patterns and MP positions in the Australian
                Senate.
              </p>
              <ul>
                <li>Click on seats to see MP details</li>
                <li>
                  Use the filter buttons to highlight different voting groups
                </li>
                <li>Switch between views for different perspectives</li>
              </ul>
            </div>
          )}
        </div>

        {selectedMp && (
          <div className="mp-details-panel">
            <button
              className="close-details"
              onClick={() => setSelectedMp(null)}
            >
              ×
            </button>
            <h2>{selectedMp.name}</h2>
            <div
              className="mp-party-badge"
              style={{ backgroundColor: partyData[selectedMp.party].color }}
            >
              {selectedMp.party}
            </div>

            <div className="mp-details-grid">
              <div className="detail-item">
                <span className="detail-label">Constituency:</span>
                <span className="detail-value">{selectedMp.constituency}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Years Served:</span>
                <span className="detail-value">{selectedMp.yearsServed}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Attendance:</span>
                <span className="detail-value">{selectedMp.attendance}%</span>
              </div>
              <div className="detail-item vote-status">
                <span className="detail-label">Vote on {selectedBill}:</span>
                <span className={`detail-value vote-${selectedMp.voteStatus}`}>
                  {selectedMp.voteStatus.charAt(0).toUpperCase() +
                    selectedMp.voteStatus.slice(1)}
                </span>
              </div>
            </div>

            <div className="voting-history">
              <h3>Recent Voting History</h3>
              <div className="history-item">
                <span className="bill-name">Climate Action Bill</span>
                <span
                  className={`vote-indicator ${
                    selectedMp.party === "Liberal" ? "oppose" : "support"
                  }`}
                >
                  {selectedMp.party === "Liberal" ? "Opposed" : "Supported"}
                </span>
              </div>
              <div className="history-item">
                <span className="bill-name">Budget 2025</span>
                <span
                  className={`vote-indicator ${
                    selectedMp.party === "Labor" ||
                    selectedMp.party === "Liberal"
                      ? "support"
                      : "oppose"
                  }`}
                >
                  {selectedMp.party === "Labor" ||
                  selectedMp.party === "Liberal"
                    ? "Supported"
                    : "Opposed"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="legend-section">
        <h3>Legend</h3>
        <div className="legend-grid">
          <div className="legend-category">
            <h4>Parties</h4>
            <div className="legend-items">
              {Object.keys(partyData).map((party) => (
                <div
                  key={party}
                  className="legend-item"
                  onMouseEnter={() => setHighlightParty(party)}
                  onMouseLeave={() => setHighlightParty(null)}
                >
                  <span
                    className="legend-color"
                    style={{ backgroundColor: partyData[party].color }}
                  ></span>
                  <span className="legend-label">{party}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="legend-category">
            <h4>Vote Status</h4>
            <div className="legend-items">
              <div
                className="legend-item"
                onMouseEnter={() => setHighlightVote("support")}
                onMouseLeave={() => setHighlightVote(null)}
              >
                <span className="legend-color support"></span>
                <span className="legend-label">Support</span>
              </div>
              <div
                className="legend-item"
                onMouseEnter={() => setHighlightVote("oppose")}
                onMouseLeave={() => setHighlightVote(null)}
              >
                <span className="legend-color oppose"></span>
                <span className="legend-label">Oppose</span>
              </div>
              <div
                className="legend-item"
                onMouseEnter={() => setHighlightVote("unknown")}
                onMouseLeave={() => setHighlightVote(null)}
              >
                <span className="legend-color unknown"></span>
                <span className="legend-label">Unknown/Abstain</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
